import {
  type ChangePaymentMethodReason,
  type MileageClaimDraft,
  MileageClaimFlow,
} from "@nmible-ltd/claims";
import { CircleAlert } from "lucide-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  PaymentType as PayableType,
  useGetMyStudyParticipantsMeStudyGet,
} from "@/api-v3";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { useParticipantSession } from "@/features/auth";
import { setReturnTo } from "@/features/payment-methods";
import { useToast } from "@/hooks/use-toast";
import { useAppDispatch, useAppSelector } from "@/store";
import { AppRoute } from "@/types/routes";

import { PwaClaimsProvider } from "../../components/pwa-claims-provider";
import { useClaimDetour } from "../../hooks/use-claim-detour";
import {
  patchMileageClaimDraft,
  selectMileageClaimDraft,
} from "../../store/mileage-claim-draft-slice";

const SubmitMileageFlow = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { toast } = useToast();
  const { participantId, currency, countryCode } = useParticipantSession();
  const startClaimDetour = useClaimDetour(PayableType.mileage_claim);
  const { data: travelRateUnit, isPending: isStudyPending } =
    useGetMyStudyParticipantsMeStudyGet({
      query: {
        select: (study) => study.travel_rate_unit,
        enabled: currency !== null,
        meta: { skipGlobalErrorToast: true },
      },
    });

  const dispatch = useAppDispatch();
  const draft = useAppSelector(selectMileageClaimDraft);
  const onDraftChange = useCallback(
    (patch: Partial<MileageClaimDraft>) =>
      dispatch(patchMileageClaimDraft(patch)),
    [dispatch]
  );

  // Only a refusal is a detour. Changing an account that can receive the claim
  // is a choice, and must not be met with a detour notice.
  const changePaymentMethod = (reason: ChangePaymentMethodReason) => {
    if (reason === "refused") {
      startClaimDetour();
      return;
    }
    dispatch(setReturnTo(pathname));
    navigate(AppRoute.PAYMENT_METHODS_LIST);
  };

  return (
    <>
      <h1 className="sr-only">{t("claim_chooseClaimMileage")}</h1>
      {currency === null || travelRateUnit === null ? (
        <Alert variant="destructive">
          <CircleAlert />
          <AlertDescription>
            {t("claim_site_details_incomplete")}
          </AlertDescription>
        </Alert>
      ) : isStudyPending ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <MileageClaimFlow
          participantId={participantId}
          currency={currency}
          countryCode={countryCode}
          defaultUnit={travelRateUnit ?? undefined}
          draft={draft}
          onDraftChange={onDraftChange}
          onCancel={() => navigate(AppRoute.PAYMENTS, { replace: true })}
          onSubmitted={() => {
            toast({ description: t("claim_toastConfirmationMessage") });
            navigate("/payments", { replace: true });
          }}
          onChangePaymentMethod={changePaymentMethod}
        />
      )}
    </>
  );
};

export const SubmitMileagePage = () => (
  <PwaClaimsProvider>
    <SubmitMileageFlow />
  </PwaClaimsProvider>
);
